import { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Camera, IdentificationCard, CheckCircle, SpinnerGap, Warning, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ScanIDDialog({ open, onClose, onDataExtracted }) {
  const [scanning, setScanning] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [error, setError] = useState(null);
  const [side, setSide] = useState('front');
  const [scannedImages, setScannedImages] = useState([]);
  const fileInputRef = useRef(null);

  const resetState = useCallback(() => {
    setScanning(false);
    setCapturedImage(null);
    setExtractedData(null);
    setError(null);
    setSide('front');
    setScannedImages([]);
  }, []);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleCapture = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64Full = event.target.result;
      const base64Data = base64Full.split(',')[1]; // Remove data:image/...;base64, prefix
      setCapturedImage(base64Full);
      setScannedImages(prev => [...prev, { side, base64: base64Data, preview: base64Full }]);

      // Send to OCR
      setScanning(true);
      setError(null);
      try {
        const { data } = await axios.post(`${API_URL}/api/patients/scan-id`, {
          image_base64: base64Data,
          side: side,
        });

        if (data.success && data.data) {
          setExtractedData(prev => {
            // Merge with previous scan data (e.g., front + back)
            if (prev) {
              const merged = { ...prev };
              Object.entries(data.data).forEach(([key, val]) => {
                if (val && (!merged[key] || merged[key] === null)) {
                  merged[key] = val;
                }
              });
              return merged;
            }
            return data.data;
          });
        } else {
          setError(data.error || 'Could not read document. Please try again with a clearer photo.');
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to process ID scan');
      } finally {
        setScanning(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleApplyData = () => {
    if (extractedData) {
      onDataExtracted(extractedData, scannedImages);
      toast.success('Patient information populated from ID scan');
      handleClose();
    }
  };

  const confidenceColor = {
    high: 'text-green-600',
    medium: 'text-yellow-600',
    low: 'text-red-600',
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="scan-id-file-input"
      />

      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IdentificationCard size={24} className="text-blue-600" />
              Scan Patient ID
            </DialogTitle>
            <DialogDescription>
              Capture a photo of the patient's Driver's License, State ID, or Passport to auto-fill demographics.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Side selection */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Document Side</Label>
              <div className="flex gap-2">
                {['front', 'back', 'passport'].map(s => (
                  <Button
                    key={s}
                    variant={side === s ? 'default' : 'outline'}
                    size="sm"
                    className={`rounded-sm capitalize ${side === s ? 'bg-slate-900' : ''}`}
                    onClick={() => setSide(s)}
                    data-testid={`scan-side-${s}`}
                  >
                    {s === 'passport' ? 'Passport' : `${s.charAt(0).toUpperCase() + s.slice(1)} of ID`}
                  </Button>
                ))}
              </div>
            </div>

            {/* Capture button */}
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 rounded-sm h-12"
              onClick={handleCapture}
              disabled={scanning}
              data-testid="capture-id-button"
            >
              {scanning ? (
                <><SpinnerGap size={20} className="mr-2 animate-spin" /> Processing...</>
              ) : (
                <><Camera size={20} className="mr-2" /> {capturedImage ? 'Scan Another Side' : 'Take Photo / Upload Image'}</>
              )}
            </Button>

            {/* Scanned images preview */}
            {scannedImages.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Scanned Images ({scannedImages.length})
                </Label>
                <div className="flex gap-2 overflow-x-auto">
                  {scannedImages.map((img, i) => (
                    <div key={i} className="relative flex-shrink-0">
                      <img
                        src={img.preview}
                        alt={`ID ${img.side}`}
                        className="w-32 h-20 object-cover rounded border border-slate-200"
                      />
                      <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center py-0.5 capitalize rounded-b">
                        {img.side}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-sm" data-testid="scan-error">
                <Warning size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Extracted data preview */}
            {extractedData && (
              <div className="space-y-3 border border-green-200 bg-green-50 rounded-sm p-4" data-testid="extracted-data-preview">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={18} className="text-green-600" />
                  <span className="text-sm font-semibold text-green-800">Information Extracted</span>
                  <span className={`text-xs ml-auto font-medium ${confidenceColor[extractedData.confidence] || 'text-slate-500'}`}>
                    Confidence: {extractedData.confidence?.toUpperCase()}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {extractedData.first_name && (
                    <div><span className="text-slate-500">First:</span> <span className="font-medium">{extractedData.first_name}</span></div>
                  )}
                  {extractedData.middle_name && (
                    <div><span className="text-slate-500">Middle:</span> <span className="font-medium">{extractedData.middle_name}</span></div>
                  )}
                  {extractedData.last_name && (
                    <div><span className="text-slate-500">Last:</span> <span className="font-medium">{extractedData.last_name}</span></div>
                  )}
                  {extractedData.dob && (
                    <div><span className="text-slate-500">DOB:</span> <span className="font-medium">{extractedData.dob}</span></div>
                  )}
                  {extractedData.gender && (
                    <div><span className="text-slate-500">Gender:</span> <span className="font-medium">{extractedData.gender}</span></div>
                  )}
                  {extractedData.address && (
                    <div className="col-span-2"><span className="text-slate-500">Address:</span> <span className="font-medium">{extractedData.address}</span></div>
                  )}
                  {extractedData.document_type && (
                    <div><span className="text-slate-500">Doc Type:</span> <span className="font-medium capitalize">{extractedData.document_type?.replace('_', ' ')}</span></div>
                  )}
                  {extractedData.document_number && (
                    <div><span className="text-slate-500">Doc #:</span> <span className="font-medium">{extractedData.document_number}</span></div>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-2">Review the data above. Click "Apply to Form" to populate the patient fields. You can edit any field after.</p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleClose} className="rounded-sm">Cancel</Button>
            {extractedData && (
              <Button
                className="bg-green-600 hover:bg-green-700 rounded-sm"
                onClick={handleApplyData}
                data-testid="apply-scan-data-button"
              >
                <CheckCircle size={18} className="mr-2" />
                Apply to Form
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
